import React, { useEffect, useState } from 'react';
import { BarChart3, ChevronDown, ChevronRight, RefreshCw } from 'lucide-react';
import api from '../../api';
import Alert from '../../components/Alert/Alert';
import { getDefaultMonthRange, getUtilizationBadgeStyle } from './auditorUtilizationUtils';

const getListFromResponse = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.content)) return payload.content;
  if (Array.isArray(payload?.data?.content)) return payload.data.content;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  return [];
};

const formatNumber = (value) => {
  const number = Number(value ?? 0);
  return Number.isFinite(number) ? number.toFixed(2).replace(/\.00$/, '') : '0';
};

const formatPercent = (value) => {
  const number = Number(value ?? 0);
  return Number.isFinite(number) ? `${number.toFixed(1).replace(/\.0$/, '')}%` : '0%';
};

const formatScheduleDates = (project) => {
  const start = String(project?.startDate || '').slice(0, 10);
  const end = String(project?.endDate || '').slice(0, 10);
  if (!start) return '-';
  return end && end !== start ? `${start} to ${end}` : start;
};

const getProjectCode = (project) => project?.projectCode || project?.code || project?.project_id || '-';
const getProjectClient = (project) => project?.clientName || project?.companyName || project?.client?.name || project?.company?.companyName || '-';
const getProjectStandard = (project) => {
  if (Array.isArray(project?.standards)) {
    const standards = project.standards
      .map((standard) => standard?.name || standard?.standardName || standard)
      .filter(Boolean);
    return standards.length > 0 ? standards.join(', ') : '-';
  }
  return project?.standardName || project?.standard || project?.scheme || '-';
};

const AuditorUtilization = () => {
  const [filters, setFilters] = useState(() => getDefaultMonthRange());
  const [auditors, setAuditors] = useState([]);
  const [expandedRows, setExpandedRows] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [alert, setAlert] = useState(null);

  const loadAuditorUtilization = async (activeFilters = filters, { silent = false } = {}) => {
    if (silent) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    try {
      setAlert(null);
      const query = new URLSearchParams();
      if (activeFilters.from) query.set('from', activeFilters.from);
      if (activeFilters.to) query.set('to', activeFilters.to);

      const response = await api.get(`/audit/auditor-utilization${query.toString() ? `?${query.toString()}` : ''}`);
      setAuditors(getListFromResponse(response?.data));
    } catch (error) {
      console.error('Error loading auditor utilization:', error);
      setAlert({
        type: 'danger',
        message: error?.response?.data?.message || 'Failed to load auditor utilization data.',
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadAuditorUtilization();
  }, []);

  const toggleExpandedRow = (userId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [userId]: !prev[userId],
    }));
  };

  const handleApplyFilters = (event) => {
    event.preventDefault();
    loadAuditorUtilization(filters);
  };

  const handleResetFilters = () => {
    const nextFilters = getDefaultMonthRange();
    setFilters(nextFilters);
    loadAuditorUtilization(nextFilters);
  };

  return (
    <div className="container-xl">
      {alert && (
        <Alert
          type={alert.type}
          message={alert.message}
          onClose={() => setAlert(null)}
        />
      )}

      <div className="row g-2 align-items-center mb-3">
        <div className="col">
          <h3 className="page-title m-0">Auditor Utilization</h3>
          <div className="text-muted mt-1">Scheduled mandays come from the same non-cancelled audit schedule rows shown in Calendar.</div>
        </div>
        <div className="col-auto ms-auto d-print-none">
          <button
            type="button"
            className="btn btn-outline-primary"
            onClick={() => loadAuditorUtilization(filters, { silent: true })}
            disabled={refreshing || loading}
          >
            <RefreshCw className="icon me-2" size={18} />
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      <div className="card mb-3">
        <div className="card-body">
          <form className="row g-3 align-items-end" onSubmit={handleApplyFilters}>
            <div className="col-md-4 col-lg-3">
              <label htmlFor="utilization-from" className="form-label">From Date</label>
              <input
                id="utilization-from"
                type="date"
                className="form-control"
                value={filters.from}
                onChange={(event) => setFilters((prev) => ({ ...prev, from: event.target.value }))}
              />
            </div>
            <div className="col-md-4 col-lg-3">
              <label htmlFor="utilization-to" className="form-label">To Date</label>
              <input
                id="utilization-to"
                type="date"
                className="form-control"
                value={filters.to}
                onChange={(event) => setFilters((prev) => ({ ...prev, to: event.target.value }))}
              />
            </div>
            <div className="col-md-4 col-lg-6 d-flex gap-2">
              <button type="submit" className="btn btn-primary">Apply Filters</button>
              <button type="button" className="btn btn-outline-secondary" onClick={handleResetFilters}>Reset</button>
            </div>
          </form>
        </div>
      </div>

      <div className="row g-3">
        <div className="col-xl-8">
          <div className="card h-100">
            <div className="card-header">
              <h3 className="card-title mb-0">Auditor Summary</h3>
            </div>
            <div className="card-body p-0">
              {loading ? (
                <div className="d-flex align-items-center justify-content-center py-5">
                  <div className="spinner-border text-primary" role="status" />
                </div>
              ) : auditors.length === 0 ? (
                <div className="p-4 text-muted text-center">No utilization data available for the selected date range.</div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-vcenter card-table table-striped mb-0">
                    <thead>
                      <tr>
                        <th style={{ width: '60px' }}>No</th>
                        <th>Auditor Name</th>
                        <th>Capacity</th>
                        <th>Confirmed</th>
                        <th>Scheduled</th>
                        <th>Available</th>
                        <th>Utilization %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {auditors.map((auditor, index) => {
                        const rowKey = auditor?.userId || `${auditor?.name || 'auditor'}-${index}`;
                        const isExpanded = !!expandedRows[rowKey];
                        const projects = Array.isArray(auditor?.projects) ? auditor.projects : [];
                        return (
                          <React.Fragment key={rowKey}>
                            <tr
                              onClick={() => toggleExpandedRow(rowKey)}
                              style={{ cursor: 'pointer' }}
                            >
                              <td className="text-muted">{index + 1}</td>
                              <td>
                                <div className="d-flex align-items-center gap-2 fw-semibold">
                                  {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                                  <span>{auditor?.name || '-'}</span>
                                </div>
                              </td>
                              <td>{auditor?.capacityConfigured ? formatNumber(auditor?.totalMandays) : <span className="text-muted">Not set</span>}</td>
                              <td>{formatNumber(auditor?.confirmedMandays)}</td>
                              <td>{formatNumber(auditor?.scheduledMandays)}</td>
                              <td>{auditor?.capacityConfigured ? formatNumber(auditor?.availableMandays) : <span className="text-muted">-</span>}</td>
                              <td>
                                <span
                                  className="badge fw-bold"
                                  style={{
                                    ...getUtilizationBadgeStyle(auditor?.utilizationPercent, auditor?.capacityConfigured),
                                    fontSize: '0.85rem',
                                    padding: '0.4em 0.75em',
                                  }}
                                >
                                  {auditor?.capacityConfigured ? formatPercent(auditor?.utilizationPercent) : 'Capacity not set'}
                                </span>
                              </td>
                            </tr>
                            {isExpanded && (
                              <tr>
                                <td colSpan="7" className="bg-light">
                                  <div className="p-3">
                                    <div className="fw-semibold mb-2">Assigned Projects</div>
                                    {projects.length === 0 ? (
                                      <div className="text-muted small">No project allocation available.</div>
                                    ) : (
                                      <div className="table-responsive">
                                        <table className="table table-sm table-bordered mb-0 bg-white">
                                          <thead>
                                            <tr>
                                              <th>Project Code</th>
                                              <th>Client</th>
                                              <th>Standard</th>
                                              <th>Mandays</th>
                                              <th>Scheduled Dates</th>
                                              <th>Role</th>
                                              <th>Status</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {projects.map((project, projectIndex) => (
                                              <tr key={project?.id || `${getProjectCode(project)}-${projectIndex}`}>
                                                <td>{getProjectCode(project)}</td>
                                                <td>{getProjectClient(project)}</td>
                                                <td>{getProjectStandard(project)}</td>
                                                <td>{formatNumber(project?.mandays || project?.usedMandays || project?.allocatedMandays)}</td>
                                                <td>{formatScheduleDates(project)}</td>
                                                <td>{project?.role || '-'}</td>
                                                <td>
                                                  <span className="badge bg-secondary">{project?.scheduleStatus || 'Booked'}</span>
                                                </td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="col-xl-4">
          <div className="card h-100">
            <div className="card-header">
              <h3 className="card-title mb-0 d-flex align-items-center gap-2">
                <BarChart3 className="icon text-primary" size={18} />
                Utilization Overview
              </h3>
            </div>
            <div className="card-body">
              {loading ? (
                <div className="d-flex align-items-center justify-content-center py-5">
                  <div className="spinner-border text-primary" role="status" />
                </div>
              ) : auditors.length === 0 ? (
                <div className="text-muted text-center py-4">No chart data available.</div>
              ) : (
                <div className="d-flex flex-column gap-3">
                  {auditors.map((auditor) => {
                    const hasCapacity = Boolean(auditor?.capacityConfigured);
                    const percentage = Number(auditor?.utilizationPercent ?? 0);
                    const badgeStyle = getUtilizationBadgeStyle(percentage, hasCapacity);
                    return (
                      <div key={auditor?.userId || auditor?.name}>
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <span className="fw-semibold small">{auditor?.name || '-'}</span>
                          <span className="badge fw-bold" style={{ ...badgeStyle, fontSize: '0.8rem', padding: '0.35em 0.65em' }}>
                            {hasCapacity ? formatPercent(percentage) : 'Capacity not set'}
                          </span>
                        </div>
                        {hasCapacity ? <div className="progress" style={{ height: '12px' }}>
                          <div
                            className="progress-bar"
                            role="progressbar"
                            style={{ width: `${Math.min(percentage, 100)}%`, backgroundColor: badgeStyle.backgroundColor }}
                            aria-valuenow={Math.min(percentage, 100)}
                            aria-valuemin="0"
                            aria-valuemax="100"
                          />
                        </div> : <div className="text-muted small">{formatNumber((Number(auditor?.confirmedMandays) || 0) + (Number(auditor?.scheduledMandays) || 0))} scheduled mandays in Calendar</div>}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuditorUtilization;
